// Pure "what does the result panel show for this selection" orchestration —
// extracted out of coverage-form.tsx (no React/state dependency) so the
// cross-insurer comparison (rankInsurers, computed here too) can never drift
// from the number the panel derives for the SAME selection. #2247 was
// exactly that: the panel and the comparison row used to be computed
// independently in two files and drifted apart. Pinning the comparison
// row's package fixed the immediate bug; this extraction removes the
// duplication that made the drift possible in the first place —
// coverage-form.tsx calls this directly, and so does the invariant test
// matrix (__tests__/insurance-panel-comparison-matrix.test.ts), so both are
// checking the SAME code, not a hand-copied mirror of it.
import type { Coverage, InsurerData, Service } from "./insurance-types"
import {
  findInsurer,
  findService,
  getCoverage,
  calculateCoverageReimbursement,
  isConfirmedCoverage,
  isEstimatedCoverage,
} from "./insurers"
import { membershipCoverageBase } from "./insurance-services"
import {
  rankInsurers,
  addonReimbursement as computeAddonReimbursement,
  type RankAddon,
  type RankedInsurer,
} from "./rank-insurers"

export type ResultType = "known" | "not_covered" | "unknown" | "likely"

export interface PanelResult {
  service: Service | undefined
  coverage: Coverage | null
  isItemized: boolean
  isKnownCoverage: boolean
  isLikelyCoverage: boolean
  isPackageNotCovered: boolean
  /** Known coverage only (fixed/percentage) — 0 for likely/itemized/unknown. */
  reimbursement: number
  /** Likely/itemized base reimbursement, before add-ons — null when N/A or
   *  when the coverage type can't produce a figure (e.g. a `likely` row with
   *  no percentage set). */
  likelyBase: number | null
  addonTotalPrice: number
  addonReimbursement: number
  /** The membership tier bought alongside the check-up, when there is one:
   *  its own price, and what the insurer pays back on it. Kept out of
   *  `addonTotalPrice` so the panel can give it its own breakdown row — it is
   *  a yearly subscription, not an add-on to a scan. */
  membershipPrice: number
  membershipReimbursement: number
  totalReimbursement: number
  likelyReimbursement: number | null
  totalPrice: number
  outOfPocket: number
  likelyOutOfPocket: number
  isConfirmedNoCoverage: boolean
  resultType: ResultType
  /** Cross-insurer comparison rows (AHD-8461), pinned to this selection. */
  ranked: RankedInsurer[]
}

export function computePanelResult(
  data: InsurerData,
  insurerId: string,
  packageId: string,
  serviceId: string,
  addons: readonly RankAddon[],
  /**
   * What the visitor actually pays for `serviceId`, when that differs from the
   * catalogue's list price — today only the member rate a selected membership
   * tier earns (`effectiveServicePrice`). Omitted everywhere else, so every
   * existing caller keeps the list price.
   *
   * It has to reach in here rather than being applied to the result: an
   * insurer reimburses a percentage of the invoice, so a member's payout is a
   * percentage of CHF 1'890, not of 1'990.
   */
  servicePrice?: number,
  /**
   * A membership tier chosen ALONGSIDE `serviceId` (never the same product —
   * when the membership is the only selection it IS `serviceId`). Its price
   * belongs in the total, because the customer is buying both, and it earns
   * its own reimbursement: `getCoverage` resolves a `membership-*` id to the
   * package's blood-test rule.
   *
   * Mechanically it is a second priced line, so it rides through
   * `computeAddonReimbursement` and `rankInsurers` next to the add-ons rather
   * than as a second base service. That keeps the comparison rows and the
   * panel on the same arithmetic for free.
   */
  membership?: { serviceId: string; price: number }
): PanelResult {
  const isOther = insurerId === "other"
  const insurer = insurerId ? findInsurer(data, insurerId) : undefined
  const service = findService(serviceId)
  const price = servicePrice ?? service?.price ?? 0
  // A membership picked on its own IS claimable: its pro forma renders the
  // bundle per visit ("Advanced blood test 299", "follow-up 200"), which is
  // the document the insurer reads. So the blood-test rule applies — to that
  // one panel, not to the tier price (`membershipCoverageBase`).
  const coverage = packageId
    ? getCoverage(data, insurerId, packageId, serviceId)
    : null
  const baseCoverageAmount = membershipCoverageBase(serviceId) ?? price

  // Itemized (Groupe Mutuel): reimbursement is a % of only the covered TARDOC
  // categories inside the package, read from the local catalog — not the
  // list price. The amount is concrete but the payout isn't confirmed by the
  // insurer, so it renders in the "likely" panel (no guarantee callout).
  const isItemized = coverage?.type === "itemized"
  const isPackageNotCovered = coverage?.type === "not_covered"

  const addonTotalPrice = addons.reduce((sum, a) => sum + a.price, 0)
  const membershipPrice = membership?.price ?? 0
  // Everything with a price and a coverage row of its own, add-ons plus the
  // membership. `getCoverage` keys on the id, and a membership id can't
  // collide with an add-on slug.
  const membershipLine: RankAddon | null = membership
    ? {
        humanReadableId: membership.serviceId,
        price: membership.price,
        // Claimed on the one panel its bundle invoices, exactly as when the
        // membership stands alone. It used to be withheld here because the
        // document dropped the tier — a pro forma bills one package and that
        // package is the check-up. The tier's panels now ride along as cover
        // lines (`membershipCoverLines`), so the insurer sees them and the
        // estimate can quote them.
        coverageBase:
          membershipCoverageBase(membership.serviceId) ?? membership.price,
      }
    : null
  const pricedLines: readonly RankAddon[] = membershipLine
    ? [...addons, membershipLine]
    : addons
  // Per-add-on reimbursement (SWICA: Calcium Score / Mamma-MRI full, DEXA
  // fixed). getCoverage resolves an add-on by its humanReadableId — the same
  // key the CMS coverage rows use; add-on slugs never collide with services.
  // Shared with rankInsurers so the panel and the comparison row can't drift
  // (this used to be a hand-copied reducer here).
  //
  // The base's own payout, needed BEFORE the lines: where a line draws on the
  // same insurer allowance as the base (a membership tier does — see
  // `addonReimbursement`) it may only claim the headroom the base leaves.
  const baseRaw =
    coverage && service && coverage.type !== "not_covered"
      ? calculateCoverageReimbursement(coverage, serviceId, baseCoverageAmount)
      : null
  const baseEnvelope =
    coverage && coverage.type !== "not_covered"
      ? { coverage, paid: baseRaw ?? 0 }
      : undefined
  const addon = packageId
    ? computeAddonReimbursement(
        data,
        insurerId,
        packageId,
        pricedLines,
        baseEnvelope
      )
    : { chf: 0, anyConfirmed: false }
  // Split back out for display: the membership rides through the same
  // machinery as the add-ons but the panel gives it its own row, so its share
  // has to be attributable. Without this the tier's payout is reported as an
  // add-on payout — the total stays right and the breakdown lies.
  //
  // Taken as the MARGINAL amount the tier adds, not as its payout in
  // isolation: the tier is last in `pricedLines`, so under a shared allowance
  // the add-ons claim their headroom first and pricing it alone would credit
  // it with headroom it never got.
  const addonsOnly = packageId
    ? computeAddonReimbursement(
        data,
        insurerId,
        packageId,
        addons,
        baseEnvelope
      )
    : { chf: 0, anyConfirmed: false }
  const addonReimbursement = addonsOnly.chf
  const membershipReimbursement = addon.chf - addonReimbursement

  // An add-on can be the ONLY thing that pays out: a SWICA customer on Core MRI
  // (not covered) who adds Mamma-MRI (covered in full) has confirmed coverage
  // for the Mamma-MRI. Deciding the outcome from the base service alone dropped
  // that payout entirely and rendered "your insurer does not cover…". The bucket
  // follows the add-on's own coverage — confirmed add-on → known, estimated
  // add-on → likely.
  const addonPaysConfirmed = addon.chf > 0 && addon.anyConfirmed
  const addonPaysEstimated = addon.chf > 0 && !addon.anyConfirmed

  const isKnownCoverage =
    (isConfirmedCoverage(coverage) || addonPaysConfirmed) && !!service
  const isLikelyCoverage =
    isEstimatedCoverage(coverage) || (!isKnownCoverage && addonPaysEstimated)

  // Known coverage only (fixed/percentage); likely and itemized are rendered
  // separately via likelyReimbursement, everything else is 0. A not_covered
  // base returns null here (calculateReimbursement does) → 0, so an add-on-only
  // payout comes through as totalReimbursement === addonReimbursement.
  const reimbursement =
    isKnownCoverage && coverage && service ? (baseRaw ?? 0) : 0
  // calculateCoverageReimbursement dispatches itemized → catalog categories,
  // likely → % of list price. Falls back to 0 (not null) when an estimated
  // add-on is carrying the payout on a base that yields no figure — otherwise
  // likelyReimbursement stays null and the amount never surfaces.
  const likelyBase =
    isLikelyCoverage && service
      ? (baseRaw ?? (addonPaysEstimated ? 0 : null))
      : null

  const totalReimbursement =
    reimbursement + addonReimbursement + membershipReimbursement
  // Add-ons count for an estimate too — rankInsurers folds them into every
  // bucket, so leaving them out here made the panel disagree with the user's
  // own row in the comparison below it.
  const likelyReimbursement =
    likelyBase !== null
      ? likelyBase + addonReimbursement + membershipReimbursement
      : null
  const totalPrice = (service ? price : 0) + addonTotalPrice + membershipPrice
  const outOfPocket = isKnownCoverage ? totalPrice - totalReimbursement : 0
  const likelyOutOfPocket =
    likelyReimbursement !== null ? totalPrice - likelyReimbursement : 0
  // Only "no coverage" when nothing pays out at all — an add-on payout on a
  // not_covered base is still a payout (see addonPaysConfirmed above).
  const isConfirmedNoCoverage =
    !isOther &&
    (insurer?.confirmedNoCoverage === true || isPackageNotCovered) &&
    addon.chf <= 0

  const resultType: ResultType = isConfirmedNoCoverage
    ? "not_covered"
    : isKnownCoverage
      ? "known"
      : isLikelyCoverage
        ? "likely"
        : "unknown"

  // Cross-insurer comparison (AHD-8461). Skip the "unknown" outcome (no
  // reimbursement to anchor the user against) and the "Other" freetext
  // insurer (not in the tree). Empty array → the section renders nothing.
  // The user's own row is pinned to the plan they picked so it always
  // matches totalReimbursement/likelyReimbursement above for the same
  // selection (see rankInsurers).
  const ranked =
    !isOther && resultType !== "unknown"
      ? rankInsurers(
          data,
          serviceId,
          pricedLines,
          packageId ? { insurerId, packageId } : undefined,
          servicePrice
        )
      : []

  return {
    service,
    coverage,
    isItemized,
    isKnownCoverage,
    isLikelyCoverage,
    isPackageNotCovered,
    reimbursement,
    likelyBase,
    addonTotalPrice,
    addonReimbursement,
    membershipPrice,
    membershipReimbursement,
    totalReimbursement,
    likelyReimbursement,
    totalPrice,
    outOfPocket,
    likelyOutOfPocket,
    isConfirmedNoCoverage,
    resultType,
    ranked,
  }
}
